import React, { useState, useEffect } from 'react';
import {
  Table, Button, Space, Input, Modal, Select, message, Tag, Row, Col,
  Card, DatePicker, Popconfirm, Descriptions, Divider, InputNumber, Form
} from 'antd';
import {
  PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined,
  CheckOutlined, CloseOutlined, ReloadOutlined
} from '@ant-design/icons';
import { purchaseOrderAPI, supplierAPI, productAPI } from '../../services/api';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const PurchaseOrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({ orderStatus: '', paymentStatus: '', receiveStatus: '' });
  const [dateRange, setDateRange] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [form] = Form.useForm();

  const orderStatusMap = {
    'draft': { text: '草稿', color: 'default' },
    'confirmed': { text: '已确认', color: 'blue' },
    'receiving': { text: '收货中', color: 'orange' },
    'completed': { text: '已完成', color: 'green' },
    'cancelled': { text: '已取消', color: 'red' }
  };

  const paymentStatusMap = {
    'unpaid': { text: '未付款', color: 'red' },
    'partial': { text: '部分付款', color: 'orange' },
    'paid': { text: '已付款', color: 'green' }
  };

  const receiveStatusMap = {
    'pending': { text: '待收货', color: 'default' },
    'partial': { text: '部分收货', color: 'orange' },
    'completed': { text: '已收货', color: 'green' }
  };

  const columns = [
    {
      title: '采购单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 150,
      fixed: 'left'
    },
    {
      title: '供应商',
      dataIndex: 'supplier',
      key: 'supplier',
      width: 150,
      render: (supplier) => supplier?.shortName || supplier?.name || '-'
    },
    {
      title: '采购日期',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 110
    },
    {
      title: '采购金额',
      dataIndex: 'finalAmount',
      key: 'finalAmount',
      width: 120,
      render: (text) => `¥${parseFloat(text || 0).toLocaleString()}`
    },
    {
      title: '订单状态',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      width: 100,
      render: (text) => {
        const status = orderStatusMap[text] || { text, color: 'default' };
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    },
    {
      title: '付款状态',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 100,
      render: (text) => {
        const status = paymentStatusMap[text] || { text, color: 'default' };
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    },
    {
      title: '收货状态',
      dataIndex: 'receiveStatus',
      key: 'receiveStatus',
      width: 100,
      render: (text) => {
        const status = receiveStatusMap[text] || { text, color: 'default' };
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    },
    {
      title: '采购员',
      dataIndex: 'purchasePerson',
      key: 'purchasePerson',
      width: 100,
      render: (person) => person?.realName || '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(record.id)}>
            详情
          </Button>
          {record.orderStatus === 'draft' && (
            <>
              <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                编辑
              </Button>
              <Popconfirm title="确认此订单?" onConfirm={() => handleConfirm(record.id)}>
                <Button type="link" icon={<CheckOutlined />} style={{ color: 'green' }}>
                  确认
                </Button>
              </Popconfirm>
            </>
          )}
          {(record.orderStatus === 'draft' || record.orderStatus === 'confirmed') && record.receiveStatus === 'pending' && (
            <Popconfirm title="取消此订单?" onConfirm={() => handleCancel(record.id)}>
              <Button type="link" danger icon={<CloseOutlined />}>
                取消
              </Button>
            </Popconfirm>
          )}
          {(record.orderStatus === 'draft' || record.orderStatus === 'cancelled') && (
            <Popconfirm title="删除此订单?" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchOrders();
  }, [pagination.current, pagination.pageSize, searchText, filters, dateRange]);

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        ...filters
      };
      if (dateRange.length === 2) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await purchaseOrderAPI.getList(params);
      setOrders(response.data);
      setPagination(prev => ({ ...prev, total: response.pagination.total }));
    } catch (error) {
      message.error('获取采购订单列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await supplierAPI.getList({ limit: 1000, status: 'active' });
      setSuppliers(response.data);
    } catch (error) {
      console.error('获取供应商列表失败', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productAPI.getList({ limit: 1000, status: 'active' });
      setProducts(response.data);
    } catch (error) {
      console.error('获取商品列表失败', error);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({ ...prev, current: newPagination.current, pageSize: newPagination.pageSize }));
  };

  const handleViewDetail = async (id) => {
    try {
      const response = await purchaseOrderAPI.getDetail(id);
      setOrderDetail(response);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('获取订单详情失败');
    }
  };

  const handleEdit = async (record) => {
    try {
      const response = await purchaseOrderAPI.getDetail(record.id);
      setEditingOrder(response);
      form.setFieldsValue({
        ...response,
        orderDate: dayjs(response.orderDate),
        expectedDate: response.expectedDate ? dayjs(response.expectedDate) : null
      });
      setOrderItems(response.items || []);
      setModalVisible(true);
    } catch (error) {
      message.error('获取订单详情失败');
    }
  };

  const handleAdd = () => {
    setEditingOrder(null);
    form.resetFields();
    form.setFieldsValue({
      orderDate: dayjs(),
      paymentMethod: 'transfer'
    });
    setOrderItems([]);
    setModalVisible(true);
  };

  const handleConfirm = async (id) => {
    try {
      await purchaseOrderAPI.confirm(id);
      message.success('订单确认成功');
      fetchOrders();
    } catch (error) {
      message.error(error.response?.data?.message || '确认失败');
    }
  };

  const handleCancel = async (id) => {
    try {
      await purchaseOrderAPI.cancel(id);
      message.success('订单取消成功');
      fetchOrders();
    } catch (error) {
      message.error(error.response?.data?.message || '取消失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await purchaseOrderAPI.delete(id);
      message.success('订单删除成功');
      fetchOrders();
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (orderItems.length === 0) {
        message.error('请添加订单明细');
        return;
      }

      const orderData = {
        ...values,
        orderDate: values.orderDate.format('YYYY-MM-DD'),
        expectedDate: values.expectedDate ? values.expectedDate.format('YYYY-MM-DD') : null,
        items: orderItems.map(item => ({
          productId: item.productId,
          productCode: item.productCode,
          productName: item.productName,
          specification: item.specification,
          unit: item.unit,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discountRate: item.discountRate || 0,
          taxRate: item.taxRate || 13
        }))
      };

      if (editingOrder) {
        await purchaseOrderAPI.update(editingOrder.id, orderData);
        message.success('订单更新成功');
      } else {
        await purchaseOrderAPI.create(orderData);
        message.success('订单创建成功');
      }

      setModalVisible(false);
      fetchOrders();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '操作失败');
      }
    }
  };

  const handleAddItem = () => {
    setOrderItems([...orderItems, {
      key: Date.now(),
      productId: null,
      productCode: '',
      productName: '',
      specification: '',
      unit: '',
      quantity: 1,
      unitPrice: 0,
      discountRate: 0,
      taxRate: 13,
      amount: 0
    }]);
  };

  const handleItemChange = (key, field, value) => {
    setOrderItems(items => items.map(item => {
      if (item.key === key || item.id === key) {
        const newItem = { ...item, [field]: value };
        if (field === 'productId') {
          const product = products.find(p => p.id === value);
          if (product) {
            newItem.productCode = product.code;
            newItem.productName = product.name;
            newItem.specification = product.model || '';
            newItem.unit = product.unit || '个';
            newItem.unitPrice = parseFloat(product.costPrice || product.unitPrice) || 0;
          }
        }
        newItem.amount = (parseFloat(newItem.quantity) || 0) * (parseFloat(newItem.unitPrice) || 0) * (1 - (parseFloat(newItem.discountRate) || 0) / 100);
        return newItem;
      }
      return item;
    }));
  };

  const handleRemoveItem = (key) => {
    setOrderItems(items => items.filter(item => (item.key || item.id) !== key));
  };

  const calculateTotal = () => {
    return orderItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setSearchText('');
    setFilters({ orderStatus: '', paymentStatus: '', receiveStatus: '' });
    setDateRange([]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const itemColumns = [
    {
      title: '商品',
      dataIndex: 'productId',
      width: 200,
      render: (value, record) => (
        <Select
          style={{ width: '100%' }}
          placeholder="选择商品"
          value={value}
          onChange={(v) => handleItemChange(record.key || record.id, 'productId', v)}
          showSearch
          optionFilterProp="children"
        >
          {products.map(p => (
            <Option key={p.id} value={p.id}>{p.code} - {p.name}</Option>
          ))}
        </Select>
      )
    },
    { title: '规格', dataIndex: 'specification', width: 120, render: (value) => value || '-' },
    { title: '单位', dataIndex: 'unit', width: 60, render: (value) => value || '-' },
    {
      title: '数量',
      dataIndex: 'quantity',
      width: 100,
      render: (value, record) => (
        <InputNumber min={1} value={value} onChange={(v) => handleItemChange(record.key || record.id, 'quantity', v)} />
      )
    },
    {
      title: '单价',
      dataIndex: 'unitPrice',
      width: 120,
      render: (value, record) => (
        <InputNumber min={0} precision={2} value={value} onChange={(v) => handleItemChange(record.key || record.id, 'unitPrice', v)} />
      )
    },
    {
      title: '折扣(%)',
      dataIndex: 'discountRate',
      width: 90,
      render: (value, record) => (
        <InputNumber min={0} max={100} value={value} onChange={(v) => handleItemChange(record.key || record.id, 'discountRate', v)} />
      )
    },
    { title: '金额', dataIndex: 'amount', width: 120, render: (value) => `¥${(value || 0).toFixed(2)}` },
    {
      title: '操作',
      width: 60,
      render: (_, record) => (
        <Button type="link" danger onClick={() => handleRemoveItem(record.key || record.id)}>删除</Button>
      )
    }
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>采购订单</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新建采购单</Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <Search placeholder="搜索采购单号" onSearch={handleSearch} style={{ width: 180 }} allowClear />
          </Col>
          <Col>
            <RangePicker value={dateRange} onChange={setDateRange} placeholder={['开始日期', '结束日期']} />
          </Col>
          <Col>
            <Select placeholder="订单状态" style={{ width: 120 }} allowClear value={filters.orderStatus || undefined} onChange={(value) => handleFilterChange('orderStatus', value)}>
              <Option value="draft">草稿</Option>
              <Option value="confirmed">已确认</Option>
              <Option value="receiving">收货中</Option>
              <Option value="completed">已完成</Option>
              <Option value="cancelled">已取消</Option>
            </Select>
          </Col>
          <Col>
            <Select placeholder="收货状态" style={{ width: 120 }} allowClear value={filters.receiveStatus || undefined} onChange={(value) => handleFilterChange('receiveStatus', value)}>
              <Option value="pending">待收货</Option>
              <Option value="partial">部分收货</Option>
              <Option value="completed">已收货</Option>
            </Select>
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
          </Col>
        </Row>
      </Card>

      <Table columns={columns} dataSource={orders} rowKey="id" loading={loading} pagination={{ ...pagination, showSizeChanger: true, showQuickJumper: true, showTotal: (total) => `共 ${total} 条记录` }} onChange={handleTableChange} scroll={{ x: 1400 }} />

      <Modal title={editingOrder ? '编辑采购订单' : '新建采购订单'} open={modalVisible} onOk={handleModalOk} onCancel={() => setModalVisible(false)} width={1000} destroyOnClose>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="supplierId" label="供应商" rules={[{ required: true, message: '请选择供应商' }]}>
                <Select placeholder="选择供应商" showSearch optionFilterProp="children">
                  {suppliers.map(s => (<Option key={s.id} value={s.id}>{s.code} - {s.name}</Option>))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="orderDate" label="采购日期" rules={[{ required: true, message: '请选择日期' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expectedDate" label="期望到货日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="paymentMethod" label="付款方式">
                <Select placeholder="选择付款方式">
                  <Option value="cash">现金</Option>
                  <Option value="transfer">银行转账</Option>
                  <Option value="check">支票</Option>
                  <Option value="acceptance">承兑汇票</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contactPerson" label="收货联系人">
                <Input placeholder="请输入联系人" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contactPhone" label="收货电话">
                <Input placeholder="请输入电话" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="deliveryAddress" label="收货地址">
            <Input placeholder="请输入收货地址" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>

        <Divider>采购明细</Divider>
        <Button type="dashed" onClick={handleAddItem} style={{ marginBottom: 16 }}><PlusOutlined /> 添加商品</Button>
        <Table columns={itemColumns} dataSource={orderItems} rowKey={(record) => record.key || record.id} pagination={false} size="small" scroll={{ x: 900 }} />
        <div style={{ textAlign: 'right', marginTop: 16, fontSize: 16 }}>
          <strong>合计金额: ¥{calculateTotal().toFixed(2)}</strong>
        </div>
      </Modal>

      <Modal title={`采购单详情 - ${orderDetail?.orderNo || ''}`} open={detailModalVisible} onCancel={() => setDetailModalVisible(false)} footer={null} width={900}>
        {orderDetail && (
          <>
            <Descriptions column={3} bordered size="small">
              <Descriptions.Item label="采购单号">{orderDetail.orderNo}</Descriptions.Item>
              <Descriptions.Item label="供应商">{orderDetail.supplier?.name}</Descriptions.Item>
              <Descriptions.Item label="采购日期">{orderDetail.orderDate}</Descriptions.Item>
              <Descriptions.Item label="订单状态"><Tag color={orderStatusMap[orderDetail.orderStatus]?.color}>{orderStatusMap[orderDetail.orderStatus]?.text}</Tag></Descriptions.Item>
              <Descriptions.Item label="付款状态"><Tag color={paymentStatusMap[orderDetail.paymentStatus]?.color}>{paymentStatusMap[orderDetail.paymentStatus]?.text}</Tag></Descriptions.Item>
              <Descriptions.Item label="收货状态"><Tag color={receiveStatusMap[orderDetail.receiveStatus]?.color}>{receiveStatusMap[orderDetail.receiveStatus]?.text}</Tag></Descriptions.Item>
              <Descriptions.Item label="收货地址" span={3}>{orderDetail.deliveryAddress || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系人">{orderDetail.contactPerson || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{orderDetail.contactPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="采购员">{orderDetail.purchasePerson?.realName || '-'}</Descriptions.Item>
            </Descriptions>

            <Divider>采购明细</Divider>
            <Table
              columns={[
                { title: '商品编码', dataIndex: 'productCode', width: 100 },
                { title: '商品名称', dataIndex: 'productName', width: 150 },
                { title: '规格', dataIndex: 'specification', width: 100 },
                { title: '单位', dataIndex: 'unit', width: 60 },
                { title: '数量', dataIndex: 'quantity', width: 80 },
                { title: '单价', dataIndex: 'unitPrice', width: 100, render: (v) => `¥${parseFloat(v).toFixed(2)}` },
                { title: '折扣', dataIndex: 'discountRate', width: 80, render: (v) => `${v || 0}%` },
                { title: '金额', dataIndex: 'amount', width: 100, render: (v) => `¥${parseFloat(v).toFixed(2)}` }
              ]}
              dataSource={orderDetail.items}
              rowKey="id"
              pagination={false}
              size="small"
            />

            <Row justify="end" style={{ marginTop: 16 }}>
              <Col>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="商品金额">¥{parseFloat(orderDetail.totalAmount).toFixed(2)}</Descriptions.Item>
                  <Descriptions.Item label="税额">¥{parseFloat(orderDetail.taxAmount).toFixed(2)}</Descriptions.Item>
                  <Descriptions.Item label="优惠">¥{parseFloat(orderDetail.discountAmount || 0).toFixed(2)}</Descriptions.Item>
                  <Descriptions.Item label="采购总额">
                    <strong style={{ fontSize: 18, color: '#1890ff' }}>¥{parseFloat(orderDetail.finalAmount).toFixed(2)}</strong>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          </>
        )}
      </Modal>
    </div>
  );
};

export default PurchaseOrderList;
